import React, { useEffect } from 'react';
import { DeviceOS } from './appLinks';

export type LocationProblem = 'denied' | 'off' | 'timeout' | 'unsupported';
export type CameraProblem = 'denied' | 'missing' | 'unsupported';

export type PermissionProblem =
	{ kind: 'location'; reason: LocationProblem } | { kind: 'camera'; reason: CameraProblem };

/** Maps a GeolocationPositionError (or anything thrown) onto what the user has to fix. */
export const locationProblemFrom = (err: any): LocationProblem => {
	if (!navigator.geolocation || !window.isSecureContext) return 'unsupported';
	if (err?.code === 1) return 'denied';
	if (err?.code === 2) return 'off';
	if (err?.code === 3) return 'timeout';
	return 'off';
};

export const cameraProblemFrom = (err: any): CameraProblem => {
	if (!navigator.mediaDevices?.getUserMedia || !window.isSecureContext) return 'unsupported';
	const name = err?.name;
	if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
	if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'missing';
	return 'denied';
};

type Copy = { title: string; why: string; steps: string[] };

const copyFor = (p: PermissionProblem, os: DeviceOS): Copy => {
	if (p.kind === 'location') {
		const why = 'Your location tells the team exactly where the problem is. A report cannot be sent without it.';
		switch (p.reason) {
			case 'denied':
				return {
					title: 'Allow location',
					why,
					steps:
						os === 'iOS'
							? [
									'Tap “aA” (or the ⋯ icon) next to the web address.',
									'Tap Website Settings → Location → Allow.',
									'Still blocked? Open the Settings app → Privacy & Security → Location Services → Safari Websites → While Using the App.',
									'Come back here. It will continue on its own.',
								]
							: [
									'Tap the icon on the left of the web address (ⓘ or ⚙).',
									'Tap Permissions → Location → Allow.',
									'Come back here. It will continue on its own.',
								],
				};
			case 'off':
				return {
					title: 'Turn on location',
					why,
					steps:
						os === 'iOS'
							? [
									'Open the Settings app → Privacy & Security → Location Services and switch it on.',
									'Come back here and tap Try again.',
								]
							: [
									'Swipe down from the top of the screen.',
									'Tap Location (GPS) to turn it on.',
									'Come back here. It will continue on its own.',
								],
				};
			case 'timeout':
				return {
					title: 'Could not find your location',
					why: 'This usually happens indoors. A report needs your location to be sent.',
					steps: ['Move closer to a window or step outside for a moment.', 'Tap Try again.'],
				};
			default:
				return {
					title: 'Open in your phone’s browser',
					why: 'This browser cannot share your location, which every report needs.',
					steps: [
						os === 'iOS' ? 'Open this page in Safari.' : 'Open this page in Chrome.',
						'Tip: tap ⋯ and choose “Open in browser”.',
					],
				};
		}
	}

	const why = 'A photo of the problem is needed so the team can see it.';
	switch (p.reason) {
		case 'denied':
			return {
				title: 'Allow camera',
				why,
				steps:
					os === 'iOS'
						? [
								'Tap “aA” (or the ⋯ icon) next to the web address.',
								'Tap Website Settings → Camera → Allow.',
								'Come back here. It will continue on its own.',
							]
						: [
								'Tap the icon on the left of the web address (ⓘ or ⚙).',
								'Tap Permissions → Camera → Allow.',
								'Come back here. It will continue on its own.',
							],
			};
		case 'missing':
			return {
				title: 'No camera found',
				why,
				steps: ['Close any other app using the camera.', 'Tap Try again.'],
			};
		default:
			return {
				title: 'Open in your phone’s browser',
				why: 'This browser cannot open the camera.',
				steps: [os === 'iOS' ? 'Open this page in Safari.' : 'Open this page in Chrome.'],
			};
	}
};

interface Props {
	problem: PermissionProblem;
	os: DeviceOS;
	onRetry: () => void;
}

/**
 * Shown when location or camera is unavailable. A web page cannot open the
 * phone's settings itself, so this gives exact steps for the user's phone and
 * retries by itself when they come back (tab visible again, or the browser
 * reports the permission flipped to "granted").
 */
const PermissionHelp = ({ problem, os, onRetry }: Props) => {
	const { title, why, steps } = copyFor(problem, os);
	const autoRetry = problem.reason === 'denied' || problem.reason === 'off';

	useEffect(() => {
		if (!autoRetry) return;
		const onVisible = () => document.visibilityState === 'visible' && onRetry();
		document.addEventListener('visibilitychange', onVisible);

		let status: PermissionStatus | null = null;
		const onChange = () => status?.state === 'granted' && onRetry();
		const name = (problem.kind === 'location' ? 'geolocation' : 'camera') as PermissionName;
		navigator.permissions
			?.query({ name })
			.then((s) => {
				status = s;
				s.addEventListener('change', onChange);
			})
			.catch(() => {});

		return () => {
			document.removeEventListener('visibilitychange', onVisible);
			status?.removeEventListener('change', onChange);
		};
	}, [autoRetry, onRetry, problem.kind]);

	return (
		<div className='flex flex-col items-center text-center w-full'>
			<div className='w-16 h-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-[1rem]'>
				<i className={`fa-solid ${problem.kind === 'location' ? 'fa-location-dot' : 'fa-camera'} text-2xl`}></i>
			</div>
			<h2 className='text-xl font-bold text-slate-900 m-0'>{title}</h2>
			<p className='text-sm text-slate-500 mt-2 mb-[1.25rem] max-w-xs'>{why}</p>
			<ol className='w-full text-left list-none p-0 m-0 flex flex-col gap-2'>
				{steps.map((s, i) => (
					<li key={i} className='flex gap-[0.75rem] items-start bg-slate-50 rounded-xl px-[0.75rem] py-2.5'>
						<span className='w-6 h-6 shrink-0 rounded-full bg-[#022c56] text-white text-xs font-bold flex items-center justify-center'>
							{i + 1}
						</span>
						<span className='text-sm text-slate-700 leading-snug pt-0.5'>{s}</span>
					</li>
				))}
			</ol>
			<button
				type='button'
				onClick={onRetry}
				className='w-full mt-[1.25rem] py-3.5 rounded-xl bg-[#022c56] text-white font-semibold text-base hover:bg-[#033a70] transition'
			>
				Try again
			</button>
		</div>
	);
};

export default PermissionHelp;
